import React, { useState, useRef, useEffect } from "react";
import BarChart from "components/charts/BarChart";
import { barChartDataDailyTraffic, barChartOptionsDailyTraffic } from "variables/charts";
import { MdArrowDropUp, MdRefresh } from "react-icons/md";
import Card from "components/card";
import { getToken } from "utils/auth";

const DailyTraffic = ({ loading, chartData, onDataRefreshed }) => {
  const [localChartData, setLocalChartData] = useState(chartData);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [refreshError, setRefreshError] = useState(null);
  const refreshTimeoutRef = useRef(null);

  // Handle refresh button click
  const handleRefresh = async () => {
    if (isRefreshing) return; // Prevent multiple simultaneous requests

    setIsRefreshing(true);
    setRefreshError(null);

    try {
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:3002";
      const token = getToken();
      const response = await fetch(`${baseUrl}/dashboard`, {
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
      });

      if (!response.ok) {
        throw new Error("Failed to refresh dashboard data");
      }

      const result = await response.json();
      const freshData = result.data;

      // Update local state with fresh data
      setLocalChartData(freshData?.monthlyConversionRateChart);

      // Call parent callback if provided
      if (onDataRefreshed) {
        onDataRefreshed(freshData);
      }

      // Show success toast
      if (window.__showToast) {
        window.__showToast("Monthly Conversion Rate refreshed successfully", "success");
      }
    } catch (err) {
      setRefreshError(err.message);
      // Show error toast
      if (window.__showToast) {
        window.__showToast(
          `Failed to refresh data: ${err.message}`,
          "error"
        );
      }
    } finally {
      setIsRefreshing(false);
      // Clear error after 5 seconds
      refreshTimeoutRef.current = setTimeout(() => {
        setRefreshError(null);
      }, 5000);
    }
  };

  // Sync local state with props when they change
  useEffect(() => {
    if (!isRefreshing) {
      setLocalChartData(chartData);
    }
  }, [chartData, isRefreshing]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (refreshTimeoutRef.current) {
        clearTimeout(refreshTimeoutRef.current);
      }
    };
  }, []);

  // API data for monthly conversion rate
  const mainValue = localChartData?.currentMonthConversionRate;
  const percent = localChartData?.percentageChange;
  const trend = localChartData?.trend;
  const arrowUp = trend === "increased";
  const percentColor = arrowUp ? "text-green-500" : "text-red-500";
  let categories = barChartOptionsDailyTraffic.xaxis.categories;
  let chartSeries = barChartDataDailyTraffic;
  if (
    localChartData &&
    Array.isArray(localChartData.categories) && localChartData.categories.length > 0 &&
    Array.isArray(localChartData.chartData) && localChartData.chartData.length > 0 &&
    localChartData.chartData[0].data
  ) {
    categories = localChartData.categories;
    chartSeries = localChartData.chartData;
  }
  const chartOptions = {
    ...barChartOptionsDailyTraffic,
    xaxis: { ...barChartOptionsDailyTraffic.xaxis, categories },
  };

  return (
    <>
      <style>{`
        @keyframes refresh-spin {
          from {
            transform: rotate(0deg);
          }
          to {
            transform: rotate(360deg);
          }
        }
        .refresh-icon-spin {
          animation: refresh-spin 1s linear infinite;
        }
      `}</style>
      <Card extra="pb-7 p-[20px]">
        <div className="flex flex-row justify-between items-start">
          <div className="ml-1 pt-2 flex flex-col">
            <p className="text-sm font-medium leading-4 text-gray-600">
              Monthly Conversion Rate
            </p>
            <p className="text-[34px] font-bold text-navy-700 dark:text-white">
              {loading ? "-" : mainValue !== undefined ? mainValue : "-"}
              <span className="text-sm font-medium leading-6 text-gray-600 ml-2">
                %
              </span>
            </p>
            <div className={`flex items-center text-sm ${arrowUp ? "text-green-500" : "text-red-500"}`}>
              {arrowUp !== undefined && (
                arrowUp ? (
                  <MdArrowDropUp className="h-5 w-5" />
                ) : (
                  <MdArrowDropUp style={{ transform: "rotate(180deg)" }} className="h-5 w-5" />
                )
              )}
              <p className="font-bold"> {percent !== undefined ? `${arrowUp ? "+" : "-"}${Math.abs(percent)}%` : "-"} </p>
            </div>
          </div>
          <button
            onClick={handleRefresh}
            disabled={isRefreshing}
            className={`!linear flex items-center justify-center rounded-lg bg-lightPrimary p-2 text-brand-500 !transition !duration-200 ${isRefreshing
              ? "opacity-50 cursor-not-allowed"
              : "hover:bg-gray-100 active:bg-gray-200 dark:bg-navy-700 dark:text-white dark:hover:bg-white/20 dark:active:bg-white/10 hover:cursor-pointer"
              }`}
            title={isRefreshing ? "Refreshing data..." : "Refresh Monthly Conversion Rate"}
          >
            <MdRefresh
              className={`h-5 w-5 ${isRefreshing ? "refresh-icon-spin" : ""}`}
            />
          </button>
        </div>

        <div className="h-[300px] w-full pt-10 pb-0">
          <BarChart
            chartData={chartSeries}
            chartOptions={chartOptions}
          />
        </div>
      </Card>
    </>
  );
};

export default DailyTraffic;
